import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  TextField,
  Autocomplete,
  Card,
  CardContent,
  IconButton,
  Chip,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LanguageIcon from '@mui/icons-material/Language';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Project } from '../../models/Project';
import { SignatoryClient, SignatoryClientRequest, ClientSearchResult } from '../../models/SignatoryClient';
import { LanguageCode } from '../../models/enums/LanguageCode';
import {
  getProjectSignatoryClient,
  setProjectSignatoryClient,
  removeProjectSignatoryClient,
  searchSignatoryClientByEmail,
} from '../../api/projects-api';
import { searchClientByEmail } from '../../api/clients-api';
import { zoullooColors } from '../../theme';
import { useLanguage } from '../../i18n/LanguageContext';

interface SignatoryClientSectionProps {
  project: Project;
  canEdit: boolean;
}

const languageOptions = [
  { code: LanguageCode.Fr, label: 'Français' },
  { code: LanguageCode.En, label: 'English' },
];

const getLanguageLabel = (code: LanguageCode | undefined): string => {
  const option = languageOptions.find(opt => opt.code === code);
  return option?.label || 'Français';
};

export const SignatoryClientSection: React.FC<SignatoryClientSectionProps> = ({
  project,
  canEdit,
}) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const queryClient = useQueryClient();

  const [signatoryClient, setSignatoryClient] = useState<SignatoryClient | null>(null);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error' | 'info'>('success');

  const [searchEmail, setSearchEmail] = useState('');
  const [searchResult, setSearchResult] = useState<ClientSearchResult | null>(null);
  const [showForm, setShowForm] = useState(false);

  const [formData, setFormData] = useState<SignatoryClientRequest>({
    email: '',
    firstName: '',
    lastName: '',
    jobTitle: '',
    language: currentLanguage || LanguageCode.Fr,
    companyId: project.companyId,
  });

  // Load existing signatory client
  useEffect(() => {
    const loadSignatoryClient = async () => {
      if (!project?.id) return;

      setLoading(true);
      try {
        const client = await getProjectSignatoryClient(project.id);
        setSignatoryClient(client);
      } catch (error) {
        console.error('Error loading signatory client:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSignatoryClient();
  }, [project?.id]);

  // Update form language when currentLanguage changes
  useEffect(() => {
    if (currentLanguage) {
      setFormData(prev => ({ ...prev, language: currentLanguage }));
    }
  }, [currentLanguage]);

  const handleSearchEmail = async () => {
    if (!searchEmail || !searchEmail.includes('@')) {
      setAlertMessage(t('signatoryClient.invalidEmail'));
      setAlertSeverity('error');
      setAlertOpen(true);
      return;
    }

    setSearching(true);
    setSearchResult(null);

    try {
      const result = await searchClientByEmail(searchEmail);
      setSearchResult(result);

      if (result.found && result.client) {
        setFormData({
          email: searchEmail,
          firstName: result.client.user.firstName || '',
          lastName: result.client.user.lastName || '',
          jobTitle: result.client.jobTitle || '',
          language: currentLanguage || LanguageCode.Fr,
          userId: result.client.userId,
          companyId: result.client.companyId || project.companyId,
        });
        setAlertMessage(t('signatoryClient.clientFound'));
        setAlertSeverity('info');
      } else {
        const existingSignatory = await searchSignatoryClientByEmail(searchEmail);

        if (existingSignatory) {
          setFormData({
            email: existingSignatory.email,
            firstName: existingSignatory.firstName || '',
            lastName: existingSignatory.lastName || '',
            jobTitle: existingSignatory.jobTitle || '',
            language: existingSignatory.language || currentLanguage || LanguageCode.Fr,
            companyId: existingSignatory.companyId || project.companyId,
          });
          setAlertMessage(t('signatoryClient.existingSignatoryFound'));
          setAlertSeverity('info');
        } else {
          setFormData({
            email: searchEmail,
            firstName: '',
            lastName: '',
            jobTitle: '',
            language: currentLanguage || LanguageCode.Fr,
            companyId: project.companyId,
          });
          setAlertMessage(t('signatoryClient.clientNotFound'));
          setAlertSeverity('info');
        }
      }
      setAlertOpen(true);
      setShowForm(true);
    } catch (error) {
      console.error('Error searching client:', error);
      setAlertMessage(t('signatoryClient.searchError'));
      setAlertSeverity('error');
      setAlertOpen(true);
    } finally {
      setSearching(false);
    }
  };

  const handleSaveSignatoryClient = async () => {
    if (!formData.email) {
      setAlertMessage(t('signatoryClient.emailRequired'));
      setAlertSeverity('error');
      setAlertOpen(true);
      return;
    }

    // If not linked to a user, require firstName and lastName
    if (!formData.userId && (!formData.firstName || !formData.lastName)) {
      setAlertMessage(t('signatoryClient.nameRequired'));
      setAlertSeverity('error');
      setAlertOpen(true);
      return;
    }

    setSaving(true);
    try {
      const result = await setProjectSignatoryClient(project.id, formData);
      setSignatoryClient(result);
      setShowForm(false);
      setSearchEmail('');
      setSearchResult(null);
      setAlertMessage(t('signatoryClient.saved'));
      setAlertSeverity('success');
      setAlertOpen(true);
      queryClient.invalidateQueries({ queryKey: ['project', project.id] });
    } catch (error) {
      console.error('Error saving signatory client:', error);
      setAlertMessage(t('signatoryClient.saveError'));
      setAlertSeverity('error');
      setAlertOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveSignatoryClient = async () => {
    setSaving(true);
    try {
      await removeProjectSignatoryClient(project.id);
      setSignatoryClient(null);
      setAlertMessage(t('signatoryClient.removed'));
      setAlertSeverity('success');
      setAlertOpen(true);
      queryClient.invalidateQueries({ queryKey: ['project', project.id] });
    } catch (error) {
      console.error('Error removing signatory client:', error);
      setAlertMessage(t('signatoryClient.removeError'));
      setAlertSeverity('error');
      setAlertOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleFormChange = (field: keyof SignatoryClientRequest) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleCancel = () => {
    setShowForm(false);
    setSearchEmail('');
    setSearchResult(null);
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      jobTitle: '',
      language: currentLanguage || LanguageCode.Fr,
      companyId: project.companyId,
    });
  };

  if (loading) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={100}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h4" color={zoullooColors.secondary.dark} gutterBottom>
        {t('signatoryClient.title')}
      </Typography>

      {/* Display current signatory client */}
      {signatoryClient && !showForm && (
        <Card variant="outlined" sx={{ mb: 2, backgroundColor: zoullooColors.background.light }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <PersonIcon color="secondary" />
                  <Typography variant="h6">
                    {signatoryClient.firstName} {signatoryClient.lastName}
                  </Typography>
                  {signatoryClient.isLinkedToUser && (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label={t('signatoryClient.linkedToUser')}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                </Box>

                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <EmailIcon fontSize="small" color="action" />
                  <Typography variant="body2">{signatoryClient.email}</Typography>
                </Box>

                {signatoryClient.jobTitle && (
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <BadgeIcon fontSize="small" color="action" />
                    <Typography variant="body2">{signatoryClient.jobTitle}</Typography>
                  </Box>
                )}

                {signatoryClient.company && (
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <BusinessIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {signatoryClient.company.tradingName || signatoryClient.company.legalName}
                    </Typography>
                  </Box>
                )}

                <Box display="flex" alignItems="center" gap={1}>
                  <LanguageIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    {getLanguageLabel(signatoryClient.language)}
                  </Typography>
                </Box>
              </Box>

              {canEdit && (
                <IconButton
                  color="error"
                  onClick={handleRemoveSignatoryClient}
                  disabled={saving}
                  title={t('signatoryClient.remove')}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Search and form for adding/editing signatory client */}
      {canEdit && !signatoryClient && !showForm && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t('signatoryClient.searchByEmail')}
          </Typography>
          <Box display="flex" gap={2} alignItems="flex-start">
            <TextField
              fullWidth
              type="email"
              label={t('signatoryClient.email')}
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearchEmail();
                }
              }}
              placeholder={t('signatoryClient.emailPlaceholder')}
              size="small"
              sx={{ maxWidth: 400 }}
            />
            <Button
              variant="contained"
              startIcon={searching ? <CircularProgress size={16} /> : <SearchIcon />}
              onClick={handleSearchEmail}
              disabled={searching || !searchEmail}
              sx={{
                backgroundColor: zoullooColors.secondary.main,
                '&:hover': { backgroundColor: zoullooColors.secondary.dark },
              }}
            >
              {t('signatoryClient.search')}
            </Button>
          </Box>
        </Box>
      )}

      {/* Form for signatory client details */}
      {showForm && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />

          {searchResult?.found && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('signatoryClient.existingClientInfo')}
            </Alert>
          )}

          {searchResult && !searchResult.found && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('signatoryClient.newContactInfo')}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('signatoryClient.email')}
                value={formData.email}
                onChange={handleFormChange('email')}
                disabled={!!searchResult?.found}
                required
                size="small"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                value={languageOptions.find(opt => opt.code === formData.language) || languageOptions[0]}
                onChange={(_, newValue) => {
                  if (newValue) {
                    setFormData(prev => ({ ...prev, language: newValue.code }));
                  }
                }}
                options={languageOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                disableClearable
                size="small"
                renderInput={(params) => (
                  <TextField {...params} label={t('signatoryClient.language')} />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('signatoryClient.firstName')}
                value={formData.firstName}
                onChange={handleFormChange('firstName')}
                disabled={!!searchResult?.found}
                required={!searchResult?.found}
                size="small"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('signatoryClient.lastName')}
                value={formData.lastName}
                onChange={handleFormChange('lastName')}
                disabled={!!searchResult?.found}
                required={!searchResult?.found}
                size="small"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('signatoryClient.jobTitle')}
                value={formData.jobTitle}
                onChange={handleFormChange('jobTitle')}
                disabled={!!searchResult?.found}
                size="small"
              />
            </Grid>
          </Grid>

          <Box display="flex" gap={2} mt={3}>
            <Button
              variant="contained"
              onClick={handleSaveSignatoryClient}
              disabled={saving}
              sx={{
                backgroundColor: zoullooColors.secondary.main,
                '&:hover': { backgroundColor: zoullooColors.secondary.dark },
              }}
            >
              {saving ? <CircularProgress size={20} /> : t('signatoryClient.save')}
            </Button>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={saving}
            >
              {t('common.cancel')}
            </Button>
          </Box>
        </Box>
      )}

      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setAlertOpen(false)}
          severity={alertSeverity}
          sx={{ width: '100%' }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default SignatoryClientSection;
